import React, { useMemo, useState } from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { EmptyState } from "~/components/ui/empty-state";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useManualFacturas } from "~/hooks/useManualFacturas";
import { supabase } from "~/integrations/supabase/client";
import TabSectionHeader from "~/pages/clients/components/client-detail/TabSectionHeader";
import { formatCurrency } from "~/utils/helpers";

// Payment assignment form schema
const paymentFormSchema = z.object({
  amount: z
    .number()
    .min(1, "El monto debe ser mayor a 0")
    .refine((val) => !isNaN(val), {
      message: "El monto debe ser un número válido",
    }),
  reference: z.string().optional(),
});

type PaymentFormValues = z.infer<typeof paymentFormSchema>;

// Payment assignment dialog component
interface PaymentAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  factura: any;
  onPaymentAssigned: () => void;
}

const PaymentAssignmentDialog: React.FC<PaymentAssignmentDialogProps> = ({
  open,
  onOpenChange,
  factura,
  onPaymentAssigned,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: {
      amount: undefined,
      reference: "",
    },
  });

  const onSubmit = async (values: PaymentFormValues) => {
    setIsSubmitting(true);
    try {
      // First, create the payment record
      const { data: paymentData, error: paymentError } = await supabase
        .from("payments")
        .insert({
          amount: values.amount,
          client: factura.client_id,
          manual_factura_id: factura.id,
          payment_method: "manual",
          reference: values.reference || "Pago manual registrado desde cliente",
        })
        .select();

      if (paymentError) {
        console.error("Error creating payment:", paymentError);
        toast.error("Error al registrar el pago");
        return;
      }

      // Calculate new paid amount using RPC function that excludes checks in portfolio
      const { data: newPaidAmount, error: paidAmountError } =
        await supabase.rpc("get_manual_factura_paid_amount", {
          factura_id: factura.id,
        });

      if (paidAmountError) {
        console.error("Error calculating paid amount:", paidAmountError);
        toast.error("Error al calcular el monto pagado");
        return;
      }

      const duesAmount = factura.dues || 0;
      const newBalance = duesAmount - (newPaidAmount || 0);

      // Update the manual factura with new paid amount and balance
      const { error: updateError } = await supabase
        .from("manual_facturas")
        .update({
          paid: newPaidAmount || 0,
          balance: newBalance,
        })
        .eq("id", factura.id);

      if (updateError) {
        console.error("Error updating manual factura:", updateError);
        // If update fails, try to rollback the payment
        await supabase.from("payments").delete().eq("id", paymentData[0].id);
        toast.error("Error al actualizar la factura");
        return;
      }

      toast.success("Pago registrado exitosamente");
      form.reset();
      onOpenChange(false);
      onPaymentAssigned();
    } catch (error) {
      console.error("Error in payment submission:", error);
      toast.error("Error al procesar el pago");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate maximum amount that can be paid (current balance)
  const maxAmount = factura?.balance || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Asignar Pago Manual</DialogTitle>
          <DialogDescription>
            Registrar un pago manual para la factura{" "}
            {factura?.doc_number || "sin número"} del cliente{" "}
            {factura?.client_name}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="space-y-4">
            {/* Factura Information */}
            <div className="space-y-2 rounded-lg bg-gray-50 p-3">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Documento:</span>
                <span className="text-sm">
                  {factura?.doc_number || "Sin número"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Saldo Pendiente:</span>
                <span className="text-sm font-bold text-red-600">
                  ${formatCurrency(maxAmount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Total Adeudado:</span>
                <span className="text-sm">
                  ${formatCurrency(factura?.dues || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Pagado:</span>
                <span className="text-sm text-green-600">
                  ${formatCurrency(factura?.paid || 0)}
                </span>
              </div>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Monto del Pago</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="1"
                          max={maxAmount}
                          placeholder="Ingrese el monto"
                          value={field.value || ""}
                          onChange={(e) =>
                            field.onChange(
                              parseFloat(e.target.value) || undefined
                            )
                          }
                        />
                      </FormControl>
                      <p className="text-xs text-gray-500">
                        Máximo: ${formatCurrency(maxAmount)}
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="reference"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Referencia (Opcional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Descripción del pago" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !form.formState.isValid}
                  >
                    {isSubmitting ? "Registrando..." : "Registrar Pago"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Function to get document type badge
const getDocTypeBadge = (docType: string | null) => {
  switch (docType) {
    case "FV":
      return (
        <Badge variant="outline" className="border-blue-600 text-blue-600">
          Factura de Venta
        </Badge>
      );
    case "PL":
      return (
        <Badge variant="outline" className="border-green-600 text-green-600">
          Pagare
        </Badge>
      );
    case "NC":
      return (
        <Badge variant="outline" className="border-purple-600 text-purple-600">
          Nota de Crédito
        </Badge>
      );
    case "ND":
      return (
        <Badge variant="outline" className="border-orange-600 text-orange-600">
          Nota de Débito
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="border-gray-600 text-gray-600">
          {docType || "N/A"}
        </Badge>
      );
  }
};

// Function to get payment status badge
const getPaymentStatusBadge = (
  dues: number | null,
  paid: number | null,
  balance: number | null
) => {
  const duesAmount = dues || 0;
  const paidAmount = paid || 0;
  const balanceAmount = balance || 0;

  if (balanceAmount <= 0) {
    return (
      <Badge
        variant="outline"
        className="border-green-600 bg-green-50 text-green-600"
      >
        Pagado
      </Badge>
    );
  } else if (paidAmount > 0) {
    return (
      <Badge
        variant="outline"
        className="border-yellow-600 bg-yellow-50 text-yellow-600"
      >
        Pago Parcial
      </Badge>
    );
  } else {
    return (
      <Badge
        variant="outline"
        className="border-red-600 bg-red-50 text-red-600"
      >
        Pendiente
      </Badge>
    );
  }
};

interface ClientCuentasPorCobrarTabProps {
  clientId: string;
}

const ClientCuentasPorCobrarTab: React.FC<ClientCuentasPorCobrarTabProps> = ({
  clientId,
}) => {
  const { manualFacturas, loading, refreshManualFacturas } =
    useManualFacturas();

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState<any>(null);

  // Filter manual facturas for this specific client
  const clientFacturas = useMemo(() => {
    return manualFacturas.filter((factura) => factura.client_id === clientId);
  }, [manualFacturas, clientId]);

  // Handle payment assignment
  const handleAssignPayment = (factura: any) => {
    setSelectedFactura(factura);
    setPaymentDialogOpen(true);
  };

  const handlePaymentAssigned = () => {
    refreshManualFacturas();

    // Dispatch events to update related components
    window.dispatchEvent(
      new CustomEvent("manual-facturas-updated", {
        detail: { clientId },
      })
    );

    window.dispatchEvent(
      new CustomEvent("payment-assigned-to-manual-factura", {
        detail: { clientId },
      })
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 p-4 pt-6 md:p-6">
        <TabSectionHeader title="Cuentas por Cobrar" />
        <TableSkeleton rows={4} columns={8} />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6 p-4 pt-6 md:p-6">
        <TabSectionHeader
          title="Cuentas por Cobrar"
          description={
            clientFacturas.length > 0
              ? `${clientFacturas.length} factura${clientFacturas.length !== 1 ? "s" : ""} pendiente${clientFacturas.length !== 1 ? "s" : ""}`
              : undefined
          }
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={refreshManualFacturas}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Actualizar
            </Button>
          }
        />

        {clientFacturas.length === 0 ? (
          <EmptyState
            title="No hay cuentas por cobrar"
            description="Este cliente no tiene facturas manuales pendientes."
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead>Documento</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Fecha Vencimiento</TableHead>
                  <TableHead>Monto Adeudado</TableHead>
                  <TableHead>Pagado</TableHead>
                  <TableHead>Saldo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientFacturas.map((factura, index) => {
                  const isOverdue =
                    factura.payment_due_date &&
                    new Date() > new Date(factura.payment_due_date) &&
                    (factura.balance || 0) > 0;

                  return (
                    <TableRow
                      key={factura.id}
                      className={`duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both ${isOverdue ? "bg-red-50 hover:bg-red-100" : ""}`}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                    >
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {factura.doc_number || "N/A"}
                          </div>
                          {factura.description && (
                            <div className="max-w-32 truncate text-xs text-gray-500">
                              {factura.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getDocTypeBadge(factura.doc_type)}</TableCell>
                      <TableCell>
                        {factura.payment_due_date ? (
                          <div
                            className={
                              isOverdue ? "font-medium text-red-600" : ""
                            }
                          >
                            {format(
                              new Date(factura.payment_due_date),
                              "dd MMM yyyy",
                              { locale: es }
                            )}
                            {isOverdue && (
                              <div className="text-xs text-red-500">
                                Vencido
                              </div>
                            )}
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">
                          ${formatCurrency(factura.dues || 0)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-green-600">
                          ${formatCurrency(factura.paid || 0)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`font-medium ${
                            (factura.balance || 0) > 0
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          ${formatCurrency(factura.balance || 0)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {getPaymentStatusBadge(
                          factura.dues,
                          factura.paid,
                          factura.balance
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {(factura.balance || 0) > 0 && (
                            <Button
                              variant="default"
                              size="sm"
                              className="flex items-center gap-1"
                              onClick={() => handleAssignPayment(factura)}
                            >
                              <Plus className="h-4 w-4" />
                              Asignar Pago
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Payment Assignment Dialog */}
      <PaymentAssignmentDialog
        open={paymentDialogOpen}
        onOpenChange={setPaymentDialogOpen}
        factura={selectedFactura}
        onPaymentAssigned={handlePaymentAssigned}
      />
    </>
  );
};

export default ClientCuentasPorCobrarTab;
